import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Perksmith brand mark — the "Earned tick" (brand-assets/DECISIONS.md).
 *
 * The symbol is the dotless i with its tick: a stem, and a check landing on top
 * of it. Nothing else. No tile, no gradient, no notches — the Coupon Tile it
 * replaces came back as PointPay's wallet icon on a reverse-image search, and
 * "navy rounded square with a letter in it" is not a thing anyone can own.
 *
 * Geometry is the 64-unit master from brand-assets/mark/perksmith-symbol.svg,
 * not a redrawing of it: stem rect 26,26 12x30 r3; tick M22 14l8 8L48 4 at
 * stroke 8, round caps and joins. Change it there and change it here together.
 *
 * The stem takes `currentColor`, so the symbol is ink on paper and white on the
 * rail without a second component. The tick is always honey, which is the one
 * rule that never bends: honey is 2.2:1 on paper and may only ever be a
 * graphic, never text and never a fill under text.
 */
export interface PerksmithMarkProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
  /** The K-02b stamp: the tick drops onto the stem and the stem flinches. */
  animated?: boolean;
  title?: string;
}

/** Documents the perksmith mark contract and behavior. */
export function PerksmithMark({
  size = 32,
  animated = false,
  title = "Perksmith",
  className,
  ...props
}: Readonly<PerksmithMarkProps>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      className={cn("shrink-0", animated && "perksmith-mark--animated", className)}
      {...props}
    >
      <title>{title}</title>
      <rect
        className="perksmith-mark__stem"
        x="26"
        y="26"
        width="12"
        height="30"
        rx="3"
        style={{ fill: "currentColor" }}
      />
      <path
        className="perksmith-mark__tick"
        d="M22 14l8 8L48 4"
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
        /* A var() in a presentation attribute does not resolve; it has to be a
           style property. This exact mistake left a diagnostic edge undrawn in
           the automation canvas for months. */
        style={{ fill: "none", stroke: "var(--color-honey-400)" }}
      />
    </svg>
  );
}

/**
 * The wordmark: live text plus one SVG, never a flattened image, so it takes the
 * stage's colour and scales with the font size.
 *
 * The ı is U+0131 — a real dotless i, not an i with the dot hidden — because the
 * tick is what takes the dot's place. Every measure is in em so one rule serves
 * 16px and 132px alike; the numbers come from the outlined master, where the
 * tick's lower arm just touches the top of the stem at a -8° tilt.
 */
export interface PerksmithLockupProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Wordmark font size in px; the descriptor is half of it and everything scales. */
  size?: number;
  /** The mono cut: the ı and its tick take the surrounding ink (thermal print). */
  mono?: boolean;
}

/**
 * The Perksmith Counter lockup (DESIGN.md §4, option L-06, register B-05/B-07):
 * the wordmark with **counter** beneath it at half size, ranged right on the h.
 *
 * This app is Perksmith Counter, so this is the cut the rail head, the sign-in
 * screen and the customer display wear. A pack that belongs to Perksmith Rewards
 * (the Reward Desk, N-05/B-03) wears {@link PerksmithRewardsLockup} instead; the
 * pack manifest carries which. Never the PNG in the app, never "Perksmith
 * Counter" typed beside the symbol, and never the word POS where a person reads it.
 */
export function PerksmithCounterLockup(props: Readonly<PerksmithLockupProps>) {
  return <PerksmithLockup descriptor="counter" {...props} />;
}

/** The Rewards cut of the same lockup, for packs that belong to Perksmith Rewards. */
export function PerksmithRewardsLockup(props: Readonly<PerksmithLockupProps>) {
  return <PerksmithLockup descriptor="rewards" {...props} />;
}

function PerksmithLockup({
  descriptor,
  size = 36,
  mono = false,
  className,
  style,
  ...props
}: Readonly<PerksmithLockupProps & { descriptor: string }>) {
  return (
    <span
      className={cn("inline-flex flex-col items-stretch", mono && "perksmith-wordmark--mono", className)}
      /*
       * The descriptor is 0.5em (register B-05: half the wordmark, always). `em`
       * resolves against THIS element, so without a font-size here it resolved
       * against the page's 16px and sat at a flat 8px however large the wordmark
       * grew — the two halves of the lockup scaled independently, which is the one
       * thing a lockup may never do.
       */
      style={{ fontSize: size, ...style }}
      {...props}
    >
      <PerksmithWordmark size={size} />
      <span className="perksmith-lockup__descriptor" aria-hidden="true">
        {descriptor}
      </span>
      <span className="sr-only">{`Perksmith ${descriptor}`}</span>
    </span>
  );
}

export interface PerksmithWordmarkProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Font size in px; everything else scales from it. */
  size?: number;
}

/** Documents the perksmith wordmark contract and behavior. */
export function PerksmithWordmark({
  size = 24,
  className,
  style,
  ...props
}: Readonly<PerksmithWordmarkProps>) {
  return (
    <span
      className={cn("perksmith-wordmark", className)}
      style={{ fontSize: size, ...style }}
      {...props}
    >
      perksm
      <span className="perksmith-wordmark__i">
        {"ı"}
        <svg className="perksmith-wordmark__tick" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M3 13l6 6L21 5" />
        </svg>
      </span>
      th
    </span>
  );
}
